import { t } from "@/i18n-js/instance";
import { getFrequencyOptions } from "@circle-react/components/Events/DetailsForm/EventTimeLocation/hooks/useRecurringSettings";
import { getTimezoneShort } from "@circle-react/helpers/dateTimeHelpers";
import { formattedDateTime } from "@circle-react/helpers/dateTimeHelpers/formattedDateTime";
import type { FormScheduleValues } from "@circle-react/types";

interface FormatRecurringScheduleOptions extends Partial<FormScheduleValues> {
  occurrences?: number;
}

export const formatRecurringSchedule = ({
  frequency,
  startsAt,
  endsAt,
  occurrences,
}: FormatRecurringScheduleOptions): string => {
  if (!frequency || !startsAt) {
    return "";
  }

  const frequencyOptions = getFrequencyOptions({
    startsAt,
    firstLetterCapital: true,
  });
  const frequencyOption = frequencyOptions.find(
    option => option.value === frequency,
  );
  const frequencyLabel = frequencyOption?.label || frequency;

  // Get time string from start time
  const startDate = new Date(startsAt);
  const startTimeString = formattedDateTime({
    dateTime: startDate,
    format: "short_time",
  });
  const timezone = getTimezoneShort(undefined, startDate) || "GMT";

  // Check ending condition and format accordingly
  if (occurrences) {
    return t("events.recurring.schedule.repeats_times_at_time", {
      frequency: frequencyLabel,
      occurrences,
      time: startTimeString,
      timezone,
    });
  }

  if (endsAt) {
    const endDate = new Date(endsAt);
    const formattedEndDate = formattedDateTime({
      dateTime: endDate,
      format: "date_time_with_short_timezone",
    });

    return t("events.recurring.schedule.ends_on_date", {
      frequency: frequencyLabel,
      endDate: formattedEndDate,
    });
  }

  // Fallback if neither occurrences nor ends_at is set
  return t("events.recurring.schedule.repeats_at_time", {
    frequency: frequencyLabel,
    time: startTimeString,
    timezone,
  });
};
